import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { PromptData, PromptVariationType } from '../../organisms/WebPromptBanner/WebPromptBanner.types';

interface FcpDetailsState {
  withHurdle: boolean;
  fcpPhoneNumber?: string;
  emailAddress?: string;
}

/** Web Prompt flyout errors: `sevType` 1 → error-sev1, 2 → error-sev2 */
export type WebPromptErrorItem = {
  message: string;
  sevType: 1 | 2;
};

export type WebPromptUpdateStatusType = 'sms' | 'phone';

export type WebPromptUpdateStatusItem = {
  type: WebPromptUpdateStatusType;
  status: boolean;
};

interface WebPromptUxlDetailsState {
  customer?: {
    contactInformation?: {
      phones?: Array<{
        country?: {
          code?: string;
        };
        number?: string;
        type?: {
          code?: string;
        };
      }>;
    };
    communicationOptions?: Array<{
      method?: {
        code?: string;
      };
      subscribed?: boolean;
      type?: {
        code?: string;
      };
    }>;
    revisionToken?: string;
  };
}

export type WebPromptBannerDetails = {
  promptData: PromptData[] | null;
  hideDuration: string;
};

interface AccountModelState {
  modelData: Record<string, unknown> | null;
  setModelData: (data: Record<string, unknown>) => void;
  clearModelData: () => void;
  isContentLoading: boolean;
  setIsContentLoading: (loading: boolean) => void;
  activeKey: string | null;
  setActiveKey: (key: string | null) => void;
  modelKey: string | null;
  setModelKey: (key: string | null) => void;
  lastModelKey: string | null;
  setLastModelKey: (key: string | null) => void;
  signInEmailSuccess?: boolean;
  setSignInEmailSuccess: (success: boolean) => void;
  lookUpEmailSuccess?: boolean;
  setLookUpEmailSuccess: (success: boolean) => void;
  tokenExpiredFlyout?: boolean;
  setTokenExpiredFlyout: (show: boolean) => void;
  pageErrorReset: boolean;
  setPageErrorReset: (reset: boolean) => void;
  lookUpMemberEmail: string | null;
  setLookUpMemberEmail: (email: string | null) => void;
  fcpDetails: FcpDetailsState;
  setFcpDetails: (value: FcpDetailsState) => void;
  showErrorContent?: boolean;
  setShowErrorContent?: (show: boolean) => void;
  validateToken?: string | null;
  setValidateToken: (token: string | null) => void;
  isFcpEnabled: boolean;
  setIsFcpEnabled: (enabled: boolean) => void;
  webPromptDetails: WebPromptUxlDetailsState | null;
  setWebPromptDetails: (response: WebPromptUxlDetailsState | null) => void;
  webPromptErrorMessage: WebPromptErrorItem[];
  setWebPromptErrorMessage: (items: WebPromptErrorItem[] | null) => void;
  webPromptMobilePhoneUniquenessError: boolean;
  setWebPromptMobilePhoneUniquenessError: (show: boolean) => void;
  cnFlyoutStep: number | null;
  setCnFlyoutStep: (step: number | null) => void;
  webPromptConsentUpdateSuccess: WebPromptUpdateStatusItem[];
  setWebPromptConsentUpdateSuccess: (updates: WebPromptUpdateStatusItem[]) => void;
  showWebPromptSuccess: boolean;
  setShowWebPromptSuccess: (value: boolean) => void;
  webPromptBannerDetails: WebPromptBannerDetails;
  setWebPromptBannerDetails: (value: WebPromptBannerDetails) => void;
  currentPromptVariation?: string | null;
  setCurrentPromptVariation: (token: string | null) => void;
}

const initialState: AccountModelState = {
  modelData: null,
  setModelData: () => {},
  clearModelData: () => {},
  isContentLoading: false,
  setIsContentLoading: () => {},
  activeKey: null,
  setActiveKey: () => {},
  modelKey: null,
  setModelKey: () => {},
  lastModelKey: null,
  setLastModelKey: () => {},
  signInEmailSuccess: false,
  setSignInEmailSuccess: () => {},
  lookUpEmailSuccess: false,
  setLookUpEmailSuccess: () => {},
  tokenExpiredFlyout: false,
  setTokenExpiredFlyout: () => {},
  pageErrorReset: false,
  setPageErrorReset: () => {},
  lookUpMemberEmail: null,
  setLookUpMemberEmail: () => {},
  fcpDetails: { withHurdle: false },
  setFcpDetails: () => {},
  showErrorContent: false,
  setShowErrorContent: () => {},
  validateToken: null,
  setValidateToken: () => {},
  isFcpEnabled: false,
  setIsFcpEnabled: () => {},
  webPromptDetails: null,
  setWebPromptDetails: () => {},
  webPromptErrorMessage: [],
  setWebPromptErrorMessage: () => {},
  webPromptMobilePhoneUniquenessError: false,
  setWebPromptMobilePhoneUniquenessError: () => {},
  cnFlyoutStep: null,
  setCnFlyoutStep: () => {},
  webPromptConsentUpdateSuccess: [],
  setWebPromptConsentUpdateSuccess: () => {},
  showWebPromptSuccess: false,
  setShowWebPromptSuccess: () => {},
  webPromptBannerDetails: {
    promptData: null,
    hideDuration: '',
  },
  setWebPromptBannerDetails: () => {},
  currentPromptVariation: null,
  setCurrentPromptVariation: () => {},
};

const accountModelStore: StateCreator<AccountModelState> = set => {
  return {
    ...initialState,
    setModelData: (data: Record<string, unknown>) => {
      set({ modelData: data });
    },
    clearModelData: () => {
      set({ modelData: null });
    },
    setIsContentLoading: (loading: boolean) => {
      set({ isContentLoading: loading });
    },
    setActiveKey: (key: string | null) => {
      set({ activeKey: key });
    },
    setModelKey: (key: string | null) => {
      set({ modelKey: key });
    },
    setLastModelKey: (key: string | null) => {
      set({ lastModelKey: key });
    },
    setSignInEmailSuccess: (success: boolean) => {
      set({ signInEmailSuccess: success });
    },
    setLookUpEmailSuccess: (success: boolean) => {
      set({ lookUpEmailSuccess: success });
    },
    setTokenExpiredFlyout: (show: boolean) => {
      set({ tokenExpiredFlyout: show });
    },
    setPageErrorReset: (reset: boolean) => {
      set({ pageErrorReset: reset });
    },
    setLookUpMemberEmail: (email: string | null) => {
      set({ lookUpMemberEmail: email });
    },
    setFcpDetails: (value: FcpDetailsState) => {
      set({ fcpDetails: value });
    },
    setShowErrorContent: (show: boolean) => {
      set({ showErrorContent: show });
    },
    setValidateToken: (token: string | null) => {
      set({ validateToken: token });
    },
    setIsFcpEnabled: (enabled: boolean) => {
      set({ isFcpEnabled: enabled });
    },
    setWebPromptDetails: (response: WebPromptUxlDetailsState | null) => {
      set({ webPromptDetails: response });
    },
    setWebPromptErrorMessage: (items: WebPromptErrorItem[] | null) => {
      set({ webPromptErrorMessage: items === null ? [] : items });
    },
    setWebPromptMobilePhoneUniquenessError: (show: boolean) => {
      set({ webPromptMobilePhoneUniquenessError: show });
    },
    setCnFlyoutStep: step => set({ cnFlyoutStep: step }),
    setWebPromptConsentUpdateSuccess: (updates: WebPromptUpdateStatusItem[]) => {
      set({ webPromptConsentUpdateSuccess: updates });
    },
    setShowWebPromptSuccess: (value: boolean) => {
      set({ showWebPromptSuccess: value });
    },
    setWebPromptBannerDetails: (value: WebPromptBannerDetails) => {
      set({ webPromptBannerDetails: value });
    },
    setCurrentPromptVariation: (value: string | null) => {
      set({ currentPromptVariation: value });
    },
  };
};

export const useAccountModelStore = createAppStore<AccountModelState>(accountModelStore, { usePersistentStore: false });
